import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {EFFECTIVENESS_CONFIG as EFFECTIVENESS_CONFIG_EU} from "./modelConfigEu";
import {EFFECTIVENESS_CONFIG as EFFECTIVENESS_CONFIG_RU} from "./modelConfigRu";
import {useEffectiveness} from "./useEffectiveness";

export const EffectivenessContext = createContext(null);

export function useEffectivenessContext() {
  const ctx = useContext(EffectivenessContext);

  if (!ctx) {
    throw new Error(
      "useEffectivenessContext must be used within EffectivenessContext.Provider",
    );
  }

  return ctx;
}

export function useEffectivenessPage() {
  const {i18n, t} = useTranslation();
  const {language} = useSelector((state) => state.common.settings);

  const langIsRu = (language || i18n.language || "en")
    .toLowerCase()
    .startsWith("ru");
  const baseEffectivenessConfig = useMemo(
    () => (langIsRu ? EFFECTIVENESS_CONFIG_RU : EFFECTIVENESS_CONFIG_EU),
    [langIsRu],
  );

  const currentLanguage = language || i18n.language || "en";
  const currencySymbol = langIsRu ? "₽" : "€";

  const [repsCount, setRepsCount] = useState(
    () =>
      baseEffectivenessConfig.workforce.number_of_medical_representatives,
  );

  useEffect(() => {
    setRepsCount(
      baseEffectivenessConfig.workforce.number_of_medical_representatives,
    );
  }, [baseEffectivenessConfig]);

  const minReps = 0;
  const maxReps =
    baseEffectivenessConfig.workforce.number_of_medical_representatives_max;

  const formatInteger = useCallback(
    (value) => {
      const locale = currentLanguage === "ru" ? "ru-RU" : "en-US";
      return Math.round(value).toLocaleString(locale);
    },
    [currentLanguage],
  );

  const formatDecimal = useCallback((value) => Number(value).toFixed(1), []);

  const formatPaybackPeriod = useCallback(
    (monthsRaw) => {
      const months = Number(monthsRaw) || 0;
      const locale = currentLanguage === "ru" ? "ru-RU" : "en-US";
      const weeks = Math.round((months * 52) / 12);
      const weekPlural = new Intl.PluralRules(locale).select(weeks);
      const weekWord = t(`page.effectiveness.payback.week_${weekPlural}`, {
        defaultValue: t("page.effectiveness.payback.week_other"),
      });
      return t("page.effectiveness.payback.period", {
        weeks,
        week_word: weekWord,
      });
    },
    [currentLanguage, t],
  );

  const effectivenessConfigWithReps = useMemo(
    () => ({
      ...baseEffectivenessConfig,
      workforce: {
        ...baseEffectivenessConfig.workforce,
        number_of_medical_representatives: Math.max(
          0,
          Math.round(Number(repsCount)) || 0,
        ),
      },
    }),
    [baseEffectivenessConfig, repsCount],
  );

  const roiFromConfig = useEffectiveness(effectivenessConfigWithReps);

  const scenarioValues = useMemo(
    () => ({
      conservative: {
        investment: roiFromConfig.TOTAL_INVESTMENT_CONSERVATIVE,
        benefits: roiFromConfig.TOTAL_BENEFITS_CONSERVATIVE,
        benefit: roiFromConfig.NET_BENEFIT_CONSERVATIVE,
        roi: roiFromConfig.ROI_CONSERVATIVE,
        months: roiFromConfig.PAYBACK_MONTHS_CONSERVATIVE,
      },
      revised: {
        investment: roiFromConfig.TOTAL_INVESTMENT_REVISED,
        benefits: roiFromConfig.TOTAL_BENEFITS_REVISED,
        benefit: roiFromConfig.NET_BENEFIT_REVISED,
        roi: roiFromConfig.ROI_REVISED,
        months: roiFromConfig.PAYBACK_MONTHS_REVISED,
      },
      optimistic: {
        investment: roiFromConfig.TOTAL_INVESTMENT_OPTIMISTIC,
        benefits: roiFromConfig.TOTAL_BENEFITS_OPTIMISTIC,
        benefit: roiFromConfig.NET_BENEFIT_OPTIMISTIC,
        roi: roiFromConfig.ROI_OPTIMISTIC,
        months: roiFromConfig.PAYBACK_MONTHS_OPTIMISTIC,
      },
    }),
    [roiFromConfig],
  );

  return useMemo(
    () => ({
      repsCount,
      setRepsCount,
      minReps,
      maxReps,
      currencySymbol,
      formatInteger,
      formatDecimal,
      formatPaybackPeriod,
      effectivenessConfigWithReps,
      roiFromConfig,
      scenarioValues,
    }),
    [
      repsCount,
      minReps,
      maxReps,
      currencySymbol,
      formatInteger,
      formatDecimal,
      formatPaybackPeriod,
      effectivenessConfigWithReps,
      roiFromConfig,
      scenarioValues,
    ],
  );
}
